<template>
  <main class="home-prototype single-film-prototype single-person-prototype legal-prototype">
    <section class="home-top-panel g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :login-path="page.loginPath"
        :show-background="false"
        content-class="home-top-panel__content"
        @open-mobile-search="openMobileSearchOverlay"
      />
    </section>

    <section class="home-content-section legal-content-section">
      <div class="container g-0">
        <div class="row g-0">
          <div class="col g-0 legal-page__body" data-testid="legal-content-column">
            <h1 class="home-section-title legal-page__title" data-testid="legal-title">
              {{ page.title }}
            </h1>

            <section
              v-for="section in page.sections"
              :key="section.id"
              class="legal-page__section"
              :data-testid="`legal-section-${section.id}`"
            >
              <h2 v-if="section.title" class="legal-page__section-title">{{ section.title }}</h2>
              <template v-for="block in section.blocks" :key="block.key">
                <p v-if="block.type === 'paragraph'" class="legal-page__paragraph">
                  {{ block.text }}
                </p>
                <p v-else-if="block.type === 'email'" class="legal-page__paragraph">
                  Email:
                  <a
                    class="legal-page__link"
                    :href="`mailto:${block.email}`"
                    data-testid="legal-contact-email"
                  >
                    {{ block.email }}
                  </a>
                </p>
                <p v-else-if="block.type === 'email-sentence'" class="legal-page__paragraph">
                  If you have any questions, suggestions, inquiries or feedback, feel free to email to
                  <a
                    class="legal-page__link"
                    :href="`mailto:${block.email}`"
                    data-testid="legal-contact-email"
                  >
                    {{ block.email }}
                  </a>
                </p>
                <p
                  v-else-if="block.type === 'updated'"
                  class="legal-page__paragraph legal-page__updated"
                  data-testid="legal-last-updated"
                >
                  {{ block.text }}
                </p>
                <p v-else-if="block.type === 'x-link'" class="legal-page__paragraph">
                  or tweet or send a message on X.com @
                  <a
                    class="legal-page__link"
                    :href="block.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="legal-x-link"
                  >
                    {{ block.text }}
                  </a>
                </p>
                <ul v-else class="legal-page__list">
                  <li v-for="item in block.items" :key="item">{{ item }}</li>
                </ul>
              </template>
            </section>
          </div>
        </div>
      </div>
    </section>

    <MobileSearchOverlay
      v-if="mobileSearchOpen"
      :open="mobileSearchOpen"
      @close="closeMobileSearchOverlay"
    />
  </main>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/home/styles.css'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'

const logoPath = '/static/spa/branding/logo.png'
const mobileSearchOpen = ref(false)
const route = useRoute()

const termsSections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    blocks: [
      paragraph('Welcome to Filmsomniac.com.'),
      paragraph('Filmsomniac is a service for tracking movies and TV shows, keeping watch history, rating and favouriting titles, creating lists, and interacting with other users.'),
      paragraph('By using Filmsomniac, you agree to these Terms of Service. If you do not agree to these terms, please do not use the service.'),
      paragraph('These terms may be updated in the future as Filmsomniac evolves.'),
    ],
  },
  {
    id: 'accounts',
    title: '2. Accounts',
    blocks: [
      paragraph('To use some features of Filmsomniac, you may need to create an account.'),
      paragraph('You are responsible for:'),
      list([
        'keeping your login credentials secure',
        'all activity that happens under your account',
        'providing accurate information when creating an account',
      ]),
      paragraph('You must not:'),
      list([
        'impersonate another person',
        'create accounts for spam or abuse',
        'attempt to gain unauthorized access to other accounts or systems',
      ]),
      paragraph('Filmsomniac reserves the right to suspend or terminate accounts that violate these terms or abuse the service.'),
    ],
  },
  {
    id: 'user-content',
    title: '3. User Content',
    blocks: [
      paragraph('Filmsomniac allows users to create and publish content, including comments, ratings, favourites, watch history, lists, profile information, and other user-generated content.'),
      paragraph('You retain ownership of the content you create.'),
      paragraph('However, by posting content on Filmsomniac, you grant Filmsomniac a non-exclusive right to display, store, reproduce, and distribute that content as part of operating the service.'),
      paragraph('You are responsible for the content you publish.'),
      paragraph('You must not post content that is illegal, abusive, threatening, hateful, infringing, spammy, or malicious.'),
      paragraph('Filmsomniac may remove content that violates these rules.'),
    ],
  },
  {
    id: 'public-activity-and-visibility',
    title: '4. Public Activity and Visibility',
    blocks: [
      paragraph('Filmsomniac is designed as a social and public-facing service.'),
      paragraph('By default, some activity may be publicly visible to other users or visitors, including watched movies and episodes, public lists, comments, profile information, and other public activity.'),
      paragraph('Some features or visibility settings may change over time as Filmsomniac develops.'),
      paragraph('Users are responsible for understanding what they choose to share publicly.'),
    ],
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use',
    blocks: [
      paragraph('You agree not to abuse or disrupt the service, scrape or mass-download data from Filmsomniac, attempt to reverse engineer or attack the platform, use harmful bots or automation, upload malicious code, or interfere with other users’ experience.'),
      paragraph('Reasonable automated access by search engines is allowed.'),
      paragraph('Filmsomniac reserves the right to limit or block access that negatively affects the service or its users.'),
    ],
  },
  {
    id: 'intellectual-property',
    title: '6. Intellectual Property',
    blocks: [
      paragraph('Filmsomniac and its original design, branding, software, and content are protected by applicable intellectual property laws.'),
      paragraph('Movie, TV show, episode, person, company, poster, backdrop, and metadata information may originate from third-party sources and remain the property of their respective owners.'),
      paragraph('Filmsomniac does not claim ownership over third-party media properties.'),
      paragraph('Data and images may be provided by external services such as TheTVDB and other metadata or artwork providers.'),
    ],
  },
  {
    id: 'third-party-services',
    title: '7. Third-Party Services',
    blocks: [
      paragraph('Filmsomniac may integrate with third-party services, including authentication providers such as Google.'),
      paragraph('Use of third-party services is also subject to the terms and privacy policies of those providers.'),
      paragraph('Filmsomniac is not responsible for third-party services, websites, or external content.'),
    ],
  },
  {
    id: 'ads-and-affiliate-links',
    title: '8. Ads and Affiliate Links',
    blocks: [
      paragraph('Filmsomniac may display advertisements, sponsored content, or affiliate links.'),
      paragraph('Some links may generate commission revenue for Filmsomniac if users make purchases through those links.'),
      paragraph('Advertising and affiliate content will be integrated into the service in a way intended to support the platform and its continued development.'),
    ],
  },
  {
    id: 'availability-and-changes',
    title: '9. Availability and Changes',
    blocks: [
      paragraph('Filmsomniac is an evolving service.'),
      paragraph('Features may change, be added, removed, or interrupted at any time.'),
      paragraph('While reasonable effort is made to keep the service available and accurate, Filmsomniac is provided “as is” without guarantees of uninterrupted availability, accuracy, or reliability.'),
    ],
  },
  {
    id: 'limitation-of-liability',
    title: '10. Limitation of Liability',
    blocks: [
      paragraph('To the maximum extent permitted by law, Filmsomniac and its operators are not liable for loss of data, service interruptions, user-generated content, third-party services, or indirect or consequential damages resulting from use of the service.'),
      paragraph('Use of Filmsomniac is at your own risk.'),
    ],
  },
  {
    id: 'termination',
    title: '11. Termination',
    blocks: [
      paragraph('Filmsomniac may suspend or terminate access to the service if these Terms are violated or if the service is abused.'),
      paragraph('Users may stop using Filmsomniac at any time.'),
    ],
  },
  {
    id: 'changes-to-these-terms',
    title: '12. Changes to These Terms',
    blocks: [
      paragraph('These Terms may be updated in the future.'),
      paragraph('Continued use of Filmsomniac after changes become effective means you accept the updated Terms.'),
    ],
  },
  {
    id: 'contact',
    title: '13. Contact',
    blocks: [
      paragraph('For questions regarding these Terms, please contact:'),
      paragraph('Filmsomniac'),
      email('contact@filmsomniac.com'),
      updated('Last updated: May 11, 2026'),
    ],
  },
]

const privacySections = [
  {
    id: 'what-is-filmsomniac',
    title: '1. What is Filmsomniac?',
    blocks: [
      paragraph('Filmsomniac is a service for tracking movies and TV shows you have watched, rating and organizing them, and optionally sharing parts of your activity with others.'),
      paragraph('Filmsomniac is operated from Europe.'),
    ],
  },
  {
    id: 'information-we-collect',
    title: '2. Information we collect',
    blocks: [
      paragraph('When you create an account or use Filmsomniac, we may collect and store information such as:'),
      list([
        'Username',
        'Email address',
        'Password hash',
        'Watch history',
        'Lists',
        'Comments',
        'Watch notes/descriptions',
        'Account settings',
        'Technical information related to login sessions and site security',
      ]),
      paragraph('We may also collect limited technical information automatically, such as:'),
      list([
        'IP address',
        'Browser type',
        'Device information',
        'Session and security logs',
      ]),
      paragraph('This information is used mainly for operating, maintaining, and securing the service.'),
    ],
  },
  {
    id: 'public-and-private-information',
    title: '3. Public and private information',
    blocks: [
      paragraph('Filmsomniac contains public profile features and social features.'),
      paragraph('The following information may currently be publicly visible to other users or visitors:'),
      list([
        'Username/profile',
        'Film watches',
        'Episode watches',
        'Public lists',
        'Comments',
      ]),
      paragraph('Comments on Filmsomniac are always public.'),
      paragraph('The following information is not publicly visible by default:'),
      list([
        'Email address',
        'Passwords',
        'Ratings',
        'Favourites',
        'Private lists',
        'Watch notes/descriptions marked as private',
        'Authentication-related information',
      ]),
      paragraph('Watch notes/descriptions are private by default unless you explicitly choose to make them public.'),
      paragraph('Some visibility features may change as Filmsomniac evolves.'),
    ],
  },
  {
    id: 'authentication',
    title: '4. Authentication',
    blocks: [
      paragraph('Filmsomniac may offer account authentication using third-party providers such as Google.'),
      paragraph('If you choose to use a third-party login provider, Filmsomniac may receive limited account information from that provider, such as your email address and basic profile information.'),
      paragraph('Filmsomniac does not receive your third-party account password.'),
    ],
  },
  {
    id: 'cookies-and-sessions',
    title: '5. Cookies and sessions',
    blocks: [
      paragraph('Filmsomniac uses cookies and similar technologies necessary for:'),
      list([
        'Keeping users signed in',
        'Maintaining sessions',
        'Security and fraud prevention',
        'Basic site functionality',
      ]),
      paragraph('Filmsomniac may also use third-party advertising services, which may use cookies or similar technologies.'),
      paragraph('A separate Cookie Policy may provide additional information.'),
    ],
  },
  {
    id: 'advertising',
    title: '6. Advertising',
    blocks: [
      paragraph('Filmsomniac may display advertisements provided by third-party advertising partners, including Google Ads.'),
      paragraph('These partners may use cookies or similar technologies to display and measure advertisements.'),
      paragraph('Filmsomniac does not sell personal user data.'),
    ],
  },
  {
    id: 'emails',
    title: '7. Emails',
    blocks: [
      paragraph('Filmsomniac may send service-related emails, such as:'),
      list([
        'Account verification emails',
        'Password reset emails',
        'Important account or service notifications',
      ]),
      paragraph('Filmsomniac may also occasionally send emails related to major new features or important updates to the service.'),
      paragraph('Filmsomniac does not send regular marketing newsletters or sell mailing lists.'),
    ],
  },
  {
    id: 'data-storage-and-security',
    title: '8. Data storage and security',
    blocks: [
      paragraph('Filmsomniac stores data on servers located in Europe.'),
      paragraph('Reasonable technical and organizational measures are used to protect user data and accounts. However, no online service can guarantee absolute security.'),
    ],
  },
  {
    id: 'third-party-services',
    title: '9. Third-party services',
    blocks: [
      paragraph('Filmsomniac may use third-party services for features such as:'),
      list([
        'Authentication',
        'Email delivery',
        'Advertising',
        'Hosting and infrastructure',
      ]),
      paragraph('These services may process limited information necessary for their operation.'),
    ],
  },
  {
    id: 'account-deletion',
    title: '10. Account deletion',
    blocks: [
      paragraph('Users may request account deletion by contacting Filmsomniac. Some information may remain in backups or server logs for a limited period of time. Public comments may remain visible after account deletion in anonymized form or where technically necessary.'),
    ],
  },
  {
    id: 'changes-to-this-privacy-policy',
    title: '11. Changes to this Privacy Policy',
    blocks: [
      paragraph('This Privacy Policy may be updated as Filmsomniac evolves.'),
      paragraph('Changes will become effective when published on this page.'),
    ],
  },
  {
    id: 'contact',
    title: '12. Contact',
    blocks: [
      paragraph('For privacy-related questions or requests, you can contact:'),
      email('contact@filmsomniac.com'),
      updated('Last updated: May 11, 2026'),
    ],
  },
]

const cookieSections = [
  {
    id: 'what-are-cookies',
    title: '1. What are cookies?',
    blocks: [
      paragraph('Cookies are small text files stored on your device when you visit a website. They help websites remember things such as whether you are logged in, your settings and preferences, security-related information, and basic analytics and functionality.'),
      paragraph('Cookies themselves do not magically know who you are. They are mostly just identifiers and settings stored by your browser.'),
    ],
  },
  {
    id: 'how-filmsomniac-uses-cookies',
    title: '2. How Filmsomniac uses cookies',
    blocks: [
      paragraph('Filmsomniac uses cookies mainly for practical functionality and site operation.'),
      paragraph('Examples include:'),
      list([
        'keeping you signed in',
        'remembering basic preferences',
        'protecting accounts and forms against abuse',
        'maintaining session and security functionality',
        'improving site performance and usability',
      ]),
      paragraph('Filmsomniac is built using technologies such as Django, which uses certain essential cookies as part of normal authentication and session handling.'),
    ],
  },
  {
    id: 'essential-cookies',
    title: '3. Essential cookies',
    blocks: [
      paragraph('Some cookies are required for the site to function properly.'),
      paragraph('These may include cookies related to:'),
      list([
        'login sessions',
        'authentication',
        'security protection',
        'CSRF protection',
        'remembering temporary session information',
      ]),
      paragraph('Without these cookies, parts of Filmsomniac may not work correctly. These cookies are considered necessary for operating the service.'),
    ],
  },
  {
    id: 'analytics-and-usage-data',
    title: '4. Analytics and usage data',
    blocks: [
      paragraph('Filmsomniac may collect limited anonymous or pseudonymous analytics information to understand:'),
      list([
        'which pages are used',
        'how the service performs',
        'which features are popular',
        'where technical problems occur',
      ]),
      paragraph('Filmsomniac does not use Google Analytics. The goal of analytics is to improve the service, not to build invasive advertising profiles.'),
    ],
  },
  {
    id: 'advertising-cookies',
    title: '5. Advertising cookies',
    blocks: [
      paragraph('Filmsomniac may display advertisements, including ads provided by third-party advertising partners such as Google Ads.'),
      paragraph('These advertising providers may use cookies or similar technologies to:'),
      list([
        'show relevant ads',
        'limit repeated advertisements',
        'measure ad performance',
      ]),
      paragraph('Third-party advertising providers may collect information according to their own privacy and cookie policies. Filmsomniac does not directly control third-party advertising cookies once those services are enabled in your browser.'),
    ],
  },
  {
    id: 'third-party-services',
    title: '6. Third-party services',
    blocks: [
      paragraph('Filmsomniac may use third-party services for features such as:'),
      list([
        'authentication / login providers',
        'email delivery',
        'embedded media',
        'advertising',
        'infrastructure and security',
      ]),
      paragraph('These services may also use cookies or similar technologies as part of their functionality.'),
    ],
  },
  {
    id: 'managing-cookies',
    title: '7. Managing cookies',
    blocks: [
      paragraph('Most web browsers allow you to:'),
      list([
        'view cookies',
        'delete cookies',
        'block cookies',
        'restrict third-party cookies',
      ]),
      paragraph('You can usually manage cookie settings from your browser preferences. Please note that blocking essential cookies may cause parts of Filmsomniac to stop functioning correctly.'),
    ],
  },
  {
    id: 'changes-to-this-cookie-policy',
    title: '8. Changes to this Cookie Policy',
    blocks: [
      paragraph('This Cookie Policy may be updated from time to time as Filmsomniac evolves, new features are added, or legal requirements change. Major updates may be announced through the service when appropriate.'),
    ],
  },
  {
    id: 'contact',
    title: '9. Contact',
    blocks: [
      paragraph('If you have questions about this Cookie Policy, please contact:'),
      paragraph('Filmsomniac'),
      email('contact@filmsomniac.com'),
      updated('Last updated: May 11, 2026'),
    ],
  },
]

const legalPages = {
  'contact-information': {
    title: 'Contact information',
    documentTitle: 'Contact information',
    loginPath: '/login?redirect=%2Fcontact',
    sections: [
      {
        id: 'contact',
        title: '',
        blocks: [
          emailSentence('contact@filmsomniac.com'),
          xLink('Filmsomniac', 'https://x.com/Filmsomniac'),
        ],
      },
    ],
  },
  'cookie-policy': {
    title: 'Cookie Policy for Filmsomniac.com',
    documentTitle: 'Cookie Policy',
    loginPath: '/login?redirect=%2Fcookies',
    sections: [
      {
        id: 'introduction',
        title: 'Introduction',
        blocks: [
          paragraph('Welcome to Filmsomniac.com.'),
        ],
      },
      ...cookieSections,
    ],
  },
  'privacy-policy': {
    title: 'Filmsomniac Privacy Policy',
    documentTitle: 'Privacy Policy',
    loginPath: '/login?redirect=%2Fprivacy',
    sections: [
      {
        id: 'introduction',
        title: 'Introduction',
        blocks: [
          paragraph('Welcome to Filmsomniac.com (“Filmsomniac”, “the service”, “we”, “us”). This Privacy Policy explains what information Filmsomniac collects, how it is used, and what information may be visible to other users or visitors.'),
          paragraph('By using Filmsomniac, you agree to this Privacy Policy.'),
        ],
      },
      ...privacySections,
    ],
  },
  'terms-of-service': {
    title: 'Filmsomniac – Terms of Service',
    documentTitle: 'Terms of Service',
    loginPath: '/login?redirect=%2Ftos',
    sections: termsSections,
  },
}

const page = computed(() => legalPages[route.name] || legalPages['terms-of-service'])

watchEffect(() => {
  setDocumentTitle(buildDocumentTitle([page.value.documentTitle]))
})

function paragraph(text) {
  return { type: 'paragraph', key: text, text }
}

function email(address) {
  return { type: 'email', key: `email:${address}`, email: address }
}

function emailSentence(address) {
  return { type: 'email-sentence', key: `email-sentence:${address}`, email: address }
}

function updated(text) {
  return { type: 'updated', key: text, text }
}

function xLink(text, href) {
  return { type: 'x-link', key: `x-link:${href}`, text, href }
}

function list(items) {
  return { type: 'list', key: items.join('|'), items }
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}
</script>
